import { CaseExtendedModel, CaseTypeEnum, ExpenseModel } from 'api/model';
import { useCaseGetCaseHook } from 'api/case/case';
import { pollersErrorModalId, refreshRate } from 'constants/AppConstants';
import ApiErrorModalIds from 'contexts/ApiCallConfiguration/ApiErrorModalIds';
import { useAssignmentsFilters } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import useModalStack from 'contexts/modal/useModalStack';
import useUpdateInterval from 'hooks/useUpdateInterval';
import React, { useContext, useEffect, useMemo } from 'react';
import {
  ApiResponse,
  RequestStatus,
  useApiCallFn,
  useResponseFn,
} from 'hooks/useApiCallFn';
import DetailedCaseFormProvider from '../DetailedCaseFormProvider';
import useCaseActions from './useCaseActions';
import useCaseAssignmentActions from './useCaseAssignmentActions';
import useEventActions from './useEventActions';
import useExpenseActions from './useExpenseActions';

interface DetailedCaseContextValue {
  /** if caseResponse == undefined, then the provided id prop was undefined, to create a new case. */
  caseResponse: ApiResponse<CaseExtendedModel | undefined>;

  onCaseAssignmentCancel(assignmentId: number): void;
  onCaseAssignmentStopOver(assignmentId: number): void;
  onPlannedCaseAssignmentStopOver(assignmentId: number): void;
  onCaseAssignmentDiscard(assignmentId: number): void;
  onCaseAssignmentApprove(assignmentId: number): void;
  onCaseAssignmentOnHold(assignmentId: number): void;
  isUpdatingAssignmentStatus: boolean;

  onCloseCase(caseId: number): void;
  closeCaseStatus: 'error' | 'idle' | 'loading' | 'success';

  onSaveExpense(expense: ExpenseModel): void;
  onDeleteExpense(expense: ExpenseModel): void;
  saveExpenseStatus: RequestStatus;
  deleteExpenseStatus: RequestStatus;

  onSaveEvent: ReturnType<typeof useEventActions>['onSaveEvent'];
  onMarkEventsAsRead: ReturnType<typeof useEventActions>['onMarkEventsAsRead'];
  onDeleteEvent: ReturnType<typeof useEventActions>['onDeleteEvent'];
  saveEventStatus: RequestStatus;
  markEventsAsReadStatus: 'error' | 'idle' | 'loading' | 'success';
  deleteEventStatus: RequestStatus;
}

const DetailedCaseContext = React.createContext<
  DetailedCaseContextValue | undefined
>(undefined);

export const useDetailedCaseContext = () => {
  const value = useContext(DetailedCaseContext);
  if (!value) {
    throw Error(
      "Can't useDetailedCaseProps() outside of DetailedCase component"
    );
  }
  return value;
};

interface Props {
  /** If left undefined, a new case will be created */
  caseId?: number;
  children: React.ReactNode;
}

const DetailedCaseProvider = ({ caseId, children }: Props) => {
  const { pop: popModal } = useModalStack();
  const { triggerAssignmentsUpdate } = useAssignmentsFilters();

  const getCase = useCaseGetCaseHook();

  const caseResponse = useResponseFn(async () => {
      if (caseId !== undefined) {
      const r = await getCase({ caseId });
        return r;
      }
      return undefined;
    },
    [caseId]
  );

  const refreshCaseApiCall = useApiCallFn(
    async (id: number) => getCase({ caseId: id }),
    undefined,
    { errorModalId: pollersErrorModalId }
  );

  const handleCaseWasUpdated = async () => {
    await caseResponse.update();
    triggerAssignmentsUpdate(new Date());
  };

  const { onCloseCase, closeCaseStatus } = useCaseActions(
    caseResponse.response,
    handleCaseWasUpdated
  );

  const {
    onCaseAssignmentApprove,
    onCaseAssignmentCancel,
    onCaseAssignmentStopOver,
    onPlannedCaseAssignmentStopOver,
    onCaseAssignmentDiscard,
    onCaseAssignmentOnHold,
    isUpdatingAssignmentStatus,
  } = useCaseAssignmentActions(caseResponse.response, handleCaseWasUpdated);

  const {
    onDeleteExpense,
    onSaveExpense,
    deleteExpenseStatus,
    saveExpenseStatus,
  } = useExpenseActions(caseResponse.response, handleCaseWasUpdated);

  const {
    onSaveEvent,
    onMarkEventsAsRead,
    onDeleteEvent,
    saveEventStatus,
    markEventsAsReadStatus,
    deleteEventStatus,
  } = useEventActions(caseResponse.response, handleCaseWasUpdated);

  const isPlaceholderCase =
    caseResponse.response?.case?.caseTypeID === CaseTypeEnum.Placeholder;
  const hasLoadedCase = caseResponse.response !== undefined;

  const { startInterval, stopInterval, isRunning } = useUpdateInterval(
    async () => {
      if (caseId !== undefined && !isPlaceholderCase) {
        const [response, error] = await refreshCaseApiCall.run(caseId);

        if (response && !error) {
          popModal(pollersErrorModalId);
          popModal(ApiErrorModalIds.ConnectionError);
          caseResponse.update(response);
        }
      }
    },
    refreshRate,
    false
  );

  useEffect(() => {
    if (hasLoadedCase && !isPlaceholderCase && !isRunning) {
      startInterval();
    }
    if (isPlaceholderCase && isRunning) {
      stopInterval();
    }

    return () => {
      stopInterval();
    };
  }, [hasLoadedCase, isPlaceholderCase, isRunning, startInterval, stopInterval]);

  const contextValue = useMemo(
    (): DetailedCaseContextValue => ({
      caseResponse,

      onCaseAssignmentCancel,
      onCaseAssignmentStopOver,
      onPlannedCaseAssignmentStopOver,
      onCaseAssignmentDiscard,
      onCaseAssignmentApprove,
      onCaseAssignmentOnHold,
      isUpdatingAssignmentStatus,

      onCloseCase,
      closeCaseStatus,

      onSaveExpense,
      onDeleteExpense,
      saveExpenseStatus,
      deleteExpenseStatus,

      onSaveEvent,
      onMarkEventsAsRead,
      onDeleteEvent,
      saveEventStatus,
      markEventsAsReadStatus,
      deleteEventStatus,
    }),
    [
      caseResponse,
      onCaseAssignmentCancel,
      onCaseAssignmentStopOver,
      onPlannedCaseAssignmentStopOver,
      onCaseAssignmentDiscard,
      onCaseAssignmentApprove,
      onCaseAssignmentOnHold,
      isUpdatingAssignmentStatus,
      onCloseCase,
      closeCaseStatus,
      onSaveExpense,
      onDeleteExpense,
      saveExpenseStatus,
      deleteExpenseStatus,
      onSaveEvent,
      onMarkEventsAsRead,
      onDeleteEvent,
      saveEventStatus,
      markEventsAsReadStatus,
      deleteEventStatus,
    ]
  );

  return (
    <DetailedCaseContext.Provider value={contextValue}>
      <DetailedCaseFormProvider>{children}</DetailedCaseFormProvider>
    </DetailedCaseContext.Provider>
  );
};

export default DetailedCaseProvider;
